// ✅ React
import React, { Fragment } from "react";

// ✅ Ant Design
import { Menu } from "antd";

// ✅ Third-party Libraries
import { v4 as uuidv4 } from "uuid";

// ✅ Custom UI Components
import H3Text from "@Uilib/h3Text";

// ✅ Constants
import Constants, { entityNameEnum } from "@Apis/constants";

export function handleFileChange({ fileListData, updateAttachment, selectedCN, getAttachmentById, setIsGetAttachment, setFileList, }) {
  const currentCN = selectedCN?.data?.credit_notes?.[0];

  const fileList = fileListData?.fileList?.map((item) => ({
    ...item,
    url: item?.response?.response?.location || item?.url,
  }));

  const attachments = fileList?.map((attachment) => ({
    url: attachment?.response?.response?.location || attachment?.url, type: attachment.type, name: attachment.name, uid: attachment.uid,
  }));

  if (fileListData?.file.status === 'done') {
    const payload = {
      entity_id: currentCN?.cn_id,
      entity_name: 'credit_note',
      attachments,
    };
    updateAttachment(payload, () => {
      getAttachmentById(currentCN?.cn_id, 'credit_note', () => {
        setIsGetAttachment(true);
      });
    });
  }
  if (fileListData?.file.status === 'removed') {
    const payload = {
      entity_id: currentCN?.cn_id,
      entity_name: 'credit_note',
      attachments,
    };
    updateAttachment(payload, () => {
      getAttachmentById(currentCN?.cn_id, 'credit_note', () => {
        setIsGetAttachment(true);
      });
    });
  }

  setFileList(attachments.length > 0 ? attachments : []);
}

export const splitChargesData = (charge) => {
  const chargeWithTaxName = charge?.filter((line) => line?.tax_info?.tax_id) || [];
  const chargeWithoutTaxName = charge?.filter((line) => !line?.tax_info?.tax_id) || [];
  return { chargeWithTaxName, chargeWithoutTaxName };
};

export const renderCharges = ({ charge, MONEY, currentCN }) => !!charge?.length && charge?.map((otherCharge, i) => (
  <div key={i} className="view-document__totals-field">
    <H3Text
      text={(
        <Fragment>
          {otherCharge?.charge_name?.toProperCase()}
          &nbsp;
          {otherCharge?.tax_info?.tax_value ? (
            <span className="table-subscript">{`tax@${otherCharge.tax_info.tax_value}%`}</span>
          ) : null}
        </Fragment>
      )}
      className="view-document__totals-field-name"
    />
    {otherCharge?.charge_amount < 0
      ? <H3Text text={`(-)${MONEY((Math.abs(otherCharge?.charge_amount)), currentCN?.org_currency_info?.currency_code)}`} className="view-document__totals-field-value danger-text" />
      : <H3Text text={MONEY((Math.abs(otherCharge?.charge_amount)), currentCN?.org_currency_info?.currency_code)} className="view-document__totals-field-value " />}
  </div>
));

export const HideAdhocApprovals = (currentCN) => {
  if (currentCN?.workflow_steps) {
    const steps = Object.values(currentCN?.workflow_steps)?.filter((step) => step !== null);
    const pendingSteps = steps?.filter((step) => step?.status === 'PENDING');
    return steps.length < 10 && pendingSteps.length === steps.length;
  }
  return false;
};

export const getMenu = ({ currentCN, isReturnSlip, downloadDocument, }) => {
  return (
    <Menu>
      {currentCN?.org_currency_info?.org_currency_id && (
        <Menu.Item>
          <H3Text
            text={`${isReturnSlip ? 'Return Slip' : 'Credit Note'} (${currentCN?.org_currency_info?.currency_code})`}
            onClick={() => downloadDocument({
              url: `${Constants.CN}/download?cn_id=${currentCN?.cn_id}&tenant_id=${currentCN?.tenant_id}&beautify=false&download_document_in_base_currency=false&doc_type=${currentCN?.doc_type}`,
              document_type: `${isReturnSlip ? 'RETURN_SLIP' : 'CREDIT_NOTE'}`,
              document_number: currentCN?.credit_note_number,
              key: uuidv4(),
            })}
            className="hide__in-mobile"
          />
        </Menu.Item>
      )}
      {(currentCN?.base_currency_info?.org_currency_id && currentCN?.org_currency_info?.org_currency_id !== currentCN?.base_currency_info?.org_currency_id) && (
        <Menu.Item>
          <H3Text
            text={`${isReturnSlip ? 'Return Slip' : 'Credit Note'} (${currentCN?.base_currency_info?.currency_code})`}
            onClick={() => downloadDocument({
              url: `${Constants.CN}/download?cn_id=${currentCN?.cn_id}&tenant_id=${currentCN?.tenant_id}&beautify=false&download_document_in_base_currency=true&doc_type=${currentCN?.doc_type}`,
              document_type: `${isReturnSlip ? 'RETURN_SLIP' : 'CREDIT_NOTE'}`,
              document_number: currentCN?.credit_note_number,
              key: uuidv4(),
            })}
            className="hide__in-mobile"
          />
        </Menu.Item>
      )}
    </Menu>
  );
}

export const getLinkedDocumentInfo = ({
  currentCN,
  isDataMaskingPolicyEnable,
  isHideCostPrice,
  isHideSellingPrice,
  setSelectedPayment,
  setShowViewPayout,
  showViewPayout,
}) => {
  let linkedDocumentsInfo = [];
  if (currentCN?.credit_note_payments?.length > 0) {
    const documentDataInfo = currentCN?.credit_note_payments?.map((item) => ({
      ...item,
      documentId: item?.payment_id,
      documentNumber: `${item?.applied_payment_type?.split('_').join(' ')}# ${item?.credit_note_number || item?.payment_id}`,
      documentTotal: item?.amount,
      documentStatus: item?.payment_status,
    }));

    linkedDocumentsInfo.push({
      entityName: entityNameEnum.OUTGOING_PAYMENTS,
      title: 'LINKED PAYMENTS',
      documentData: documentDataInfo,
      entityNumberPrefix: '',
      isDrawer: true,
      drawerState: showViewPayout,
      drawerCallback: (payment, isOpen) => {
        setSelectedPayment(payment);
        setShowViewPayout(isOpen);
      },
      entityVisible: documentDataInfo?.length > 0 && !(isDataMaskingPolicyEnable && (isHideCostPrice || isHideSellingPrice)),
      showEntityTotal: true,
    });
  }
  if (currentCN?.applied_invoices?.length > 0) {
    const documentDataInfo = currentCN?.applied_invoices?.map((item) => ({
      documentId: item?.invoice_id,
      documentNumber: item?.invoice_number,
      documentTotal: item?.amount,
      documentStatus: item?.status,
    }));

    linkedDocumentsInfo.push({
      entityName: entityNameEnum.INVOICE,
      title: 'APPLIED INVOICES',
      documentData: documentDataInfo,
      entityNumberPrefix: '',
      isRedirect: true,
      redirectPath: '/sales/invoice/view/',
      entityVisible: documentDataInfo?.length > 0,
      showEntityTotal: true,
      hidePrice: isDataMaskingPolicyEnable && isHideCostPrice,
      popOverMessage: "You don't have access to view amount",
    });
  }
  return linkedDocumentsInfo;
}